import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogPosts } from '@/mocks/blogPosts';
import WhatsAppFloating from '@/components/feature/WhatsAppFloating';

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
}

function RevealSection({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [menuOpen, setMenuOpen] = useState(false);

  const post = blogPosts.find((p) => p.slug === slug);
  const relatedPosts = blogPosts.filter((p) => p.slug !== slug).slice(0, 2);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | Blog WZap PRO`;
    } else {
      document.title = 'Artigo não encontrado | Blog WZap PRO';
    }
    window.scrollTo(0, 0);
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4">
        <div className="w-16 h-16 rounded-full bg-foreground-100 flex items-center justify-center mb-6">
          <i className="ri-article-line text-3xl text-foreground-400" />
        </div>
        <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">Artigo não encontrado</h1>
        <p className="text-foreground-500 mb-6 text-center max-w-md">O artigo que você procura não está disponível ou foi removido.</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-6 py-3 rounded-xl transition-all duration-200 whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" />
          Voltar para o início
        </Link>
      </div>
    );
  }

  const schemaJsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.image,
    "datePublished": post.date,
    "author": {
      "@type": "Organization",
      "name": "WZap PRO"
    },
    "publisher": {
      "@type": "Organization",
      "name": "WZap PRO",
      "url": "https://wzappro.com"
    }
  };

  return (
    <div className="min-h-screen bg-background-50 overflow-x-hidden">
      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaJsonLd) }}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-background-100/80 backdrop-blur-md border-b border-background-200/60 dark:border-background-300/30">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-[68px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
              <i className="ri-whatsapp-fill text-white text-lg" />
            </div>
            <span className="font-heading font-bold text-foreground-950 dark:text-foreground-100 text-[17px] tracking-tight">WZap PRO</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm text-foreground-600 dark:text-foreground-400 hover:text-foreground-900 dark:hover:text-foreground-200 transition-colors whitespace-nowrap">Início</Link>
            <a href="/#blog" className="text-sm text-foreground-600 dark:text-foreground-400 hover:text-foreground-900 dark:hover:text-foreground-200 transition-colors whitespace-nowrap">Blog</a>
            <Link to="/contact" className="text-sm text-foreground-600 dark:text-foreground-400 hover:text-foreground-900 dark:hover:text-foreground-200 transition-colors whitespace-nowrap">Contato</Link>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="text-sm font-medium text-foreground-700 dark:text-foreground-300 hover:text-foreground-950 dark:hover:text-foreground-100 transition-colors whitespace-nowrap"
            >
              Entrar
            </Link>
            <Link
              to="/login"
              className="text-sm font-medium bg-primary-500 hover:bg-primary-600 text-white px-5 py-2.5 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md active:scale-[0.98] whitespace-nowrap"
            >
              Testar Grátis
            </Link>
          </div>

          {/* Mobile menu */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
          </button>
        </div>

        {menuOpen && (
          <div className="md:hidden bg-white/95 dark:bg-background-100/95 backdrop-blur-md border-b border-background-200/60 px-5 py-4 space-y-3">
            <Link to="/" className="block text-sm text-foreground-700 hover:text-primary-600 transition-colors" onClick={() => setMenuOpen(false)}>Início</Link>
            <Link to="/#blog" className="block text-sm text-foreground-700 hover:text-primary-600 transition-colors" onClick={() => setMenuOpen(false)}>Blog</Link>
            <Link to="/contact" className="block text-sm text-foreground-700 hover:text-primary-600 transition-colors" onClick={() => setMenuOpen(false)}>Contato</Link>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="relative pt-[68px] min-h-[340px] md:min-h-[400px] flex items-end bg-gradient-to-br from-[#0d3b2e] via-[#128C7E] to-[#1a5f4a]">
        <div className="absolute inset-0 overflow-hidden opacity-20">
          <div className="absolute -top-20 -left-20 w-[400px] h-[400px] rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-20 -right-20 w-[300px] h-[300px] rounded-full bg-[#25D366]/20 blur-3xl" />
        </div>

        <div className="relative z-10 w-full max-w-4xl mx-auto px-5 md:px-8 pb-10 md:pb-14">
          <RevealSection>
            <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors mb-6">
              <i className="ri-arrow-left-line" />
              Voltar para o blog
            </Link>
          </RevealSection>
          <RevealSection delay={100}>
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-xs font-bold text-white/90">
                {post.category}
              </span>
              <span className="text-xs text-white/50">{post.date}</span>
              <span className="text-xs text-white/50">•</span>
              <span className="text-xs text-white/50">{post.readTime} de leitura</span>
            </div>
          </RevealSection>
          <RevealSection delay={200}>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight tracking-tight">
              {post.title}
            </h1>
          </RevealSection>
          <RevealSection delay={300}>
            <p className="text-base md:text-lg text-white/60 max-w-3xl mt-4 leading-relaxed">
              {post.excerpt}
            </p>
          </RevealSection>
        </div>
      </section>

      {/* Featured Image */}
      <section className="max-w-4xl mx-auto px-5 md:px-8 -mt-10 relative z-10">
        <RevealSection>
          <div className="rounded-2xl overflow-hidden shadow-sm border border-background-200">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-auto object-cover"
              style={{ maxHeight: '420px' }}
            />
          </div>
        </RevealSection>
      </section>

      {/* Content */}
      <main className="py-10 md:py-16 px-5 md:px-8">
        <article className="max-w-3xl mx-auto">
          <RevealSection>
            <div
              className="prose prose-lg max-w-none 
                prose-headings:font-heading prose-headings:font-bold prose-headings:text-foreground-950
                prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-foreground-600 prose-p:leading-relaxed prose-p:mb-5
                prose-strong:text-foreground-900 prose-strong:font-semibold
                prose-li:text-foreground-600 prose-li:mb-1
                prose-ol:my-5 prose-ul:my-5
                prose-ol:pl-5 prose-ul:pl-5
                dark:prose-headings:text-foreground-100
                dark:prose-p:text-foreground-400
                dark:prose-strong:text-foreground-200
                dark:prose-li:text-foreground-400"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </RevealSection>

          {/* Share */}
          <RevealSection delay={200}>
            <div className="mt-12 pt-8 border-t border-background-200">
              <p className="text-sm font-medium text-foreground-700 mb-4">Compartilhe este artigo:</p>
              <div className="flex items-center gap-3">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(post.title + ' - ' + window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-[#25D366] flex items-center justify-center hover:scale-110 transition-transform cursor-pointer"
                  aria-label="Compartilhar no WhatsApp"
                >
                  <i className="ri-whatsapp-fill text-white text-lg" />
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-[#1877F2] flex items-center justify-center hover:scale-110 transition-transform cursor-pointer"
                  aria-label="Compartilhar no Facebook"
                >
                  <i className="ri-facebook-fill text-white text-lg" />
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-foreground-900 dark:bg-foreground-100 flex items-center justify-center hover:scale-110 transition-transform cursor-pointer"
                  aria-label="Compartilhar no Twitter"
                >
                  <i className="ri-twitter-x-fill text-white dark:text-foreground-950 text-lg" />
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-[#0A66C2] flex items-center justify-center hover:scale-110 transition-transform cursor-pointer"
                  aria-label="Compartilhar no LinkedIn"
                >
                  <i className="ri-linkedin-fill text-white text-lg" />
                </a>
              </div>
            </div>
          </RevealSection>
        </article>
      </main>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-12 md:py-16 bg-background-100 px-5 md:px-8">
          <div className="max-w-4xl mx-auto">
            <RevealSection>
              <div className="mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">Artigos Relacionados</h2>
                <p className="text-sm text-foreground-500">Continue aprendendo sobre WhatsApp Marketing</p>
              </div>
            </RevealSection>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {relatedPosts.map((rp, i) => (
                <RevealSection key={rp.slug} delay={i * 100}>
                  <Link
                    to={`/blog/${rp.slug}`}
                    className="group bg-white dark:bg-background-50 rounded-2xl border border-background-200 dark:border-background-300 overflow-hidden flex flex-col md:flex-row hover:border-primary-200 dark:hover:border-primary-500/30 transition-all duration-300"
                  >
                    <div className="md:w-44 h-40 md:h-auto flex-shrink-0 overflow-hidden">
                      <img
                        src={rp.image}
                        alt={rp.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-5 flex flex-col justify-center flex-1">
                      <span className="text-[10px] font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wider mb-2">{rp.category}</span>
                      <h3 className="text-sm font-heading font-semibold text-foreground-950 dark:text-foreground-100 mb-2 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                        {rp.title}
                      </h3>
                      <div className="flex items-center gap-2 text-[11px] text-foreground-400">
                        <span>{rp.date}</span>
                        <span>•</span>
                        <span>{rp.readTime} de leitura</span>
                      </div>
                    </div>
                  </Link>
                </RevealSection>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-12 md:py-16 px-5 md:px-8 bg-primary-500">
        <div className="max-w-3xl mx-auto text-center">
          <RevealSection>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
              Pronto para começar suas campanhas?
            </h2>
            <p className="text-white/70 text-base mb-6 max-w-xl mx-auto">
              Teste o WZap PRO gratuitamente por 7 dias e veja como é fácil fazer WhatsApp Marketing profissional.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 bg-white hover:bg-white/90 text-foreground-950 text-sm font-bold px-8 py-3.5 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md active:scale-[0.98] whitespace-nowrap"
            >
              Criar Conta Gratuita
              <i className="ri-arrow-right-line" />
            </Link>
          </RevealSection>
        </div>
      </section>

      {/* WhatsApp Floating */}
      <WhatsAppFloating />

      {/* Footer */}
      <footer className="bg-background-100 dark:bg-background-100 border-t border-background-200 dark:border-background-300 py-12 px-5 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                <i className="ri-whatsapp-fill text-white text-base" />
              </div>
              <span className="font-heading font-bold text-foreground-950 dark:text-foreground-100 text-base">WZap PRO</span>
            </Link>
            <div className="flex flex-wrap justify-center gap-4 text-xs text-foreground-400">
              <Link to="/privacy" className="hover:text-foreground-600 transition-colors">Privacidade</Link>
              <span className="text-foreground-300">•</span>
              <Link to="/terms" className="hover:text-foreground-600 transition-colors">Termos</Link>
              <span className="text-foreground-300">•</span>
              <Link to="/faq" className="hover:text-foreground-600 transition-colors">FAQ</Link>
              <span className="text-foreground-300">•</span>
              <Link to="/about" className="hover:text-foreground-600 transition-colors">Sobre</Link>
              <span className="text-foreground-300">•</span>
              <Link to="/contact" className="hover:text-foreground-600 transition-colors">Contato</Link>
            </div>
            <p className="text-xs text-foreground-400 text-center">
              &copy; {new Date().getFullYear()} WZap PRO. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}